import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { DateTimeTooltip } from "src/view/conponents/DateTimeTooltip";
import { formatDisplayDateTime } from "src/utils/dateTime";
import { HBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import { IconAutorenew } from "@ui/icons/IconAutorenew";
import { IconManageAccounts } from "@ui/icons/IconManageAccounts";
import { IconPayments } from "@ui/icons/IconPayments";
import { IconReceiptLong } from "@ui/icons/IconReceiptLong";
import { IconStorefront } from "@ui/icons/IconStorefront";
import Panel from "@ui/panel/Panel";
import Pre from "@ui/pre/Pre";
import { TabItem, Tabs } from "@ui/tabs/Tabs";
import { Typo } from "@ui/text/Typo";
import { RecursiveCSS } from "@ui/theme/GlobalTheme";
import Signal from "badmfck-signal";
import { useState } from "react";
import { GD } from "src/GD";
import { SETTINGS_ALLOWED_IDS } from "src/App";
import { notify } from "../../tools/notify";
import { IClient } from "src/services/ClientService";
import { ClientProducts } from "../ClientProducts";
import { Fees, FeesRates } from "../Fees";
import { createName, FormParser } from "../FormParser";
import { parseClientStatusPayload } from "./clientStatusHistory";
import { ClientTrafficRules } from "../pages/administration/ClientTrafficRules";
import { hasRole } from "src/permissions";
import { ArrowRight, Network, Percent, Server, Settings as SettingsIcon, X } from "lucide-react";
import { Settings } from "../forms/Settings";
import { StatusHistory } from "./StatusHistory";
import styled from "styled-components";

export const S_CLIENT_SETTINGS_POPUP = new Signal<{ client: IClient; section?: TSection; onUpdate: () => void } | null>("S_CLIENT_SETTINGS_POPUP");

enum TABS {
	GENERAL = 0,
	FEES = 1,
	TRAFFIC_RULES = 2,
	SFTP = 3,
}

const SECTIONS = ["rolling", "status", "rates", "setup_fees", "products", "statements", "reports"] as const;
type TSection = (typeof SECTIONS)[number];

const sectionIcons: Record<TSection, React.FC<IconClip>> = {
	rolling: IconAutorenew,
	status: IconManageAccounts,
	rates: IconReceiptLong,
	setup_fees: IconPayments,
	products: IconStorefront,
	statements: IconReceiptLong,
	reports: IconReceiptLong,
};

const SettingsHeader = styled.div`
	flex: 0 0 auto;
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 20px;
	padding: 20px 24px 16px;
	border-bottom: 1px solid #edf0f5;
	background: #fff;
	font:
		13px/1.5 "Euclid Circular B",
		sans-serif;
	color: #344054;
	.identity {
		min-width: 0;
	}
	.eyebrow {
		color: #667085;
		font-size: 11px;
		margin-bottom: 5px;
	}
	h2 {
		margin: 0;
		font-size: 21px;
		font-weight: 600;
		line-height: 1.3;
		color: #182230;
		overflow-wrap: anywhere;
	}
	button {
		display: grid;
		place-items: center;
		flex: 0 0 32px;
		width: 32px;
		height: 32px;
		padding: 0;
		border: 0;
		border-radius: 7px;
		color: #667085;
		background: transparent;
		cursor: pointer;
	}
	button:hover {
		background: #f5f7fa;
	}
`;

const SettingsTabs = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	min-height: 0;
	background: #fff;
	> div:first-child {
		flex: 0 0 auto;
		overflow-x: auto;
	}
	> div:last-child {
		min-height: 0;
		background: #fff;
		padding: 20px 24px 24px;
	}
	> div:last-child:has(.client-sftp-content) {
		background: #f5f7fa;
	}
	@media (max-width: 600px) {
		> div:last-child {
			padding: 16px;
		}
	}
	> div:last-child:has(.client-settings-split) {
		padding: 0;
		overflow: hidden;
	}
`;

const SftpContent = styled.div`
	/* The tab owns the background and scrolling; keep only the form's white card. */
	> div {
		padding: 0 !important;
		border-radius: 0 !important;
		background-color: transparent !important;
		max-width: none !important;
	}
`;

export const ClientSettingsPopup = () => {
	useLocale();
	const request = S_CLIENT_SETTINGS_POPUP.use();
	const [busy, setBusy] = useState(false);
	const [state, setState] = useState<"full" | "mid">("mid");
	const [user] = GD.REQ_PROFILE.useValue(undefined, undefined);
	const [project] = GD.REQ_CURRENT_PROJECT.useRequest();

	if (!request) return null;

	const { client, section: initialSection } = request;

	const closePopup = () => {
		setState("mid");
		S_CLIENT_SETTINGS_POPUP.invoke(null);
	};

	const getSelectedMainTab = (): TABS => {
		if (initialSection === "status") return TABS.GENERAL;
		if (initialSection === "rolling" || initialSection === "setup_fees" || initialSection === "rates") return TABS.FEES;
		return TABS.GENERAL;
	};

	const sx: RecursiveCSS = {
		minWidth: 0,
		width: state === "full" ? "1200px" : "860px",
		maxWidth: "94vw",
		height: "88dvh",
		minHeight: 0,
		maxHeight: "88dvh",
		padding: "0",
		borderRadius: "12px",
		overflow: "hidden",
		backgroundColor: "#fff",
		transition: "width .2s",
	};

	return (
		<Panel sx={sx} busy={busy} decorator="absolute" growContent disableScroll onClose={closePopup}>
			<SettingsHeader>
				<div className="identity">
					<div className="eyebrow">{msg("clientSettings")}</div>
					<h2>{client.name}</h2>
				</div>
				<button type="button" aria-label={msg("closeClientSettings")} onClick={closePopup}>
					<X size={18} />
				</button>
			</SettingsHeader>
			<SettingsTabs>
				<Tabs
					scrollable
					variant="modern"
					spaceBottom={0}
					selected={getSelectedMainTab()}
					onSectionSelect={section => {
						if (section === TABS.GENERAL || section === TABS.SFTP) {
							setState("mid");
						} else if (section === TABS.FEES || section === TABS.TRAFFIC_RULES) {
							setState("full");
						}
					}}>
					<TabItem title={msg("general")} icon={<SettingsIcon size={16} />} section={TABS.GENERAL}>
						<GeneralTab
							key="general"
							sections={{
								status: <StatusBlock client={client} setBusy={setBusy} onSuccess={() => request?.onUpdate?.()} />,
								products: <ClientProducts client={client} />,
								...(user && SETTINGS_ALLOWED_IDS.includes(user.id) ? { statements: <StatementsBlock client={client} /> } : {}),
								...(user && SETTINGS_ALLOWED_IDS.includes(user.id) ? { reports: <ReportsBlock client={client} /> } : {}),
							}}
							initialSection={initialSection}
						/>
					</TabItem>
					<TabItem title={msg("feesAndRollings")} icon={<Percent size={16} />} section={TABS.FEES}>
						<GeneralTab
							key="fees_and_rollings"
							sections={{
								rolling: <Fees target_id={client.id} type="rollings" target_type="client" key={2} />,
								setup_fees: <Fees target_id={client.id} type="setup" target_type="client" key={3} />,
								rates: <FeesRates target_id={client.id} target_type="client" key={4} />,
							}}
							initialSection={initialSection}
						/>
					</TabItem>
					<TabItem title={msg("trafficRules")} icon={<Network size={16} />} section={TABS.TRAFFIC_RULES}>
						{project && (
							<ClientTrafficRules
								key={`${project.id}:${client.id}`}
								clientId={client.id}
								clientName={client.name}
								projectId={project.id}
								userId={user?.id}
								canManage={hasRole(user?.projects.find(item => item.id === project.id)?.roles, "admin")}
							/>
						)}
					</TabItem>
					<TabItem title={msg("generalSettings")} icon={<Server size={16} />} section={TABS.SFTP}>
						<SftpContent className="client-sftp-content">
							<Settings
								size="small"
								key={`client-settings:${client.id}`}
								target_id={client.id}
								target_type="client"
								type="client_settings"
								appearance="modern"
							/>
						</SftpContent>
					</TabItem>
				</Tabs>
			</SettingsTabs>
		</Panel>
	);
};

const GeneralTab = ({ sections, initialSection }: { sections: Partial<Record<TSection, React.ReactNode>>; initialSection?: TSection }) => {
	useLocale();
	const [section, setSection] = useState<TSection>((initialSection && initialSection in sections ? initialSection : Object.keys(sections)[0]) as TSection);

	const content = sections[section] || null;

	return (
		<div
			className="client-settings-split"
			style={{ display: "flex", flexDirection: "column", position: "relative", flex: "1 1 0", minHeight: 0, overflow: "hidden" }}>
			<HBox sx={{ flex: "1 1 0", minHeight: 0, overflow: "hidden" }}>
				<VBox sx={{ overflowY: "auto", minHeight: 0, width: "185px", flexShrink: 0, paddingTop: "16px", borderRight: "1px solid #edf0f5" }}>
					{Object.keys(sections).map(sec => (
						<HBox
							key={sec}
							sx={{
								borderLeft: "5px solid " + (sec === section ? "rgb(0, 145, 255)" : "transparent"),
								borderBottom: "1px solid rgba(0,0,0,0.1)",
								cursor: "pointer",
								flexShrink: 0,
								padding: "20px",
								gap: "10px",
								alignItems: "center",
								whiteSpace: "nowrap",
								overflow: "hidden",
							}}
							onClick={() => setSection(sec as TSection)}>
							{(() => {
								const Icon = sectionIcons[sec as TSection];
								return Icon ? <Icon color={sec === section ? "rgb(0, 145, 255)" : "#666"} width={24} height={24} /> : null;
							})()}
							<Typo
								bold={sec === section}
								variant="medium"
								sx={{
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
									textTransform: "capitalize",
								}}>
								{createName(sec)}
							</Typo>
						</HBox>
					))}
				</VBox>
				<VBox
					sx={{
						flex: "1 1 0",
						minWidth: 0,
						minHeight: 0,
						overflow: "auto",
						backgroundColor: "#f5f7fa",
						padding: "16px",
						"& > *": { flexShrink: 0 },
					}}>
					{content}
				</VBox>
			</HBox>
		</div>
	);
};

interface IClientStatusModel {
	client_status: {
		status: string;
		reason: string;
	} & Record<string, any>;
}

const getChangeStatusModel = async (status: string): Promise<IClientStatusModel & Record<string, any>> => {
	const statuses = await GD.B_CLIENT_STATUSES.execute({});
	let statusesValues: string[] = [];
	if (statuses?.data) {
		statusesValues = statuses.data.map(s => s.name);
	}

	return {
		client_status: {
			status: status,
			$__status_type: "select",
			$__status_values: statusesValues,
			reason: "",
			$__reason_type: "textarea",
		},
	};
};

const StatusBlock = ({ client, setBusy, onSuccess }: { client: IClient; setBusy: (busy: boolean) => void; onSuccess: () => void }) => {
	useLocale();
	const historyRequest = {
		method: "events/list",
		data: {
			target_id: client.id,
			target_type: "client",
			action: "client_status_change",
		},
	};

	const [history, repeatHistory] = GD.REQ_API.useRequest(undefined, historyRequest);
	const historyItems = Array.isArray(history?.data) ? history.data : [];

	return (
		<VBox gap="10px">
			<Typo sx={{ marginBottom: "2px", color: "#667085", fontSize: "11px", fontWeight: 600, letterSpacing: ".04em", textTransform: "uppercase" }}>{msg("changeStatus")}</Typo>
			<FormParser<IClientStatusModel>
				primaryButton={{ title: msg("changeStatus") }}
				appearance="modern"
				size="small"
				sx={{ padding: 0, borderRadius: 0, backgroundColor: "transparent", maxWidth: "none" }}
				secondaryButton={null}
				key="statuses"
				onSubmit={async (data: IClientStatusModel): Promise<any> => {
					setBusy(true);
					const response = await GD.REQ_API.request({
						method: "clients/status/change",
						data: {
							client_id: client.id,
							status: (data as any).client_status.status,
							reason: (data as any).client_status.reason,
						},
					});

					if (response.error) {
						//	console.log("!!!---Error changing status", JSON.stringify(response.error));
						setBusy(false);
						return JSON.parse(JSON.stringify(response));
					}
					setBusy(false);
					repeatHistory(historyRequest);
					GD.S_CLIENTS_UPDATE.invoke({});
					notify.success(msg("statusUpdated"));
					onSuccess();

					return data;
					// возвращает data: true;
					//return response;
				}}
				onRequest={async (): Promise<IClientStatusModel> => {
					return getChangeStatusModel(client.status);
				}}
			/>

			<StatusHistory aria-label={msg("statusHistory_5e75e48f")}>
				<h3>{msg("statusChangeHistory")}</h3>
				<div className="entries">
					{historyItems.map((event: any) => {
						const payload = parseClientStatusPayload(event.data);
						return (
							<article key={event.id}>
								<div className="meta">
									<span className="author">{event.user_name}</span>
									<time dateTime={event.created_at}>
										<DateTimeTooltip value={event.created_at}>{formatDisplayDateTime(event.created_at, { separator: " " })}</DateTimeTooltip>
									</time>
								</div>
								<div className="transition">
									<span className="status">{payload.old_status || "—"}</span>
									<ArrowRight size={16} strokeWidth={1.5} color="#98a2b3" aria-hidden="true" />
									<span className="status" data-status={payload.new_status}>
										{payload.new_status || "—"}
									</span>
								</div>
								{payload.reason && <p>{payload.reason}</p>}
							</article>
						);
					})}
					{historyItems.length === 0 && <div className="empty">{msg("noStatusChangesYet")}</div>}
				</div>
			</StatusHistory>
		</VBox>
	);
};

const ReportsBlock = ({ client }: { client: IClient }) => {
	useLocale();
	const [result, setResult] = useState<any>(null);
	const [busy, setBusy] = useState(false);

	const generate = async () => {
		setBusy(true);
		const response = await GD.REQ_API.request({
			method: "reports/force",
			data: { client_id: client.id },
		});
		setResult(response);
		setBusy(false);
	};

	return (
		<VBox gap="10px">
			<Button title={msg("forceReport")} onClick={generate} disabled={busy} />
			{result && <Pre data={result} />}
		</VBox>
	);
};

const StatementsBlock = ({ client }: { client: IClient }) => {
	useLocale();
	const [result, setResult] = useState<any>(null);
	const [busy, setBusy] = useState(false);

	const generate = async () => {
		setBusy(true);
		const response = await GD.REQ_API.request({
			method: "statements/generate",
			data: { client_id: client.id },
		});
		setResult(response);
		setBusy(false);
	};

	return (
		<VBox gap="10px">
			<Button title={msg("generateStatement")} onClick={generate} disabled={busy} />
			{result && <Pre data={result} />}
		</VBox>
	);
};
